import { SvyIcon } from '@innovorder/savory';
import { useCustomNavigate } from 'hooks/useCustomNavigate/useCustomNavigate';
import { useRedirectToHome } from 'hooks/useRedirectHome/useRedirectHome';
import { useWorkflowStateMachine } from 'hooks/useWorkflowStateMachine';
import { TranslationKey } from 'locales/fr';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
    MargedButton,
    MargedCaption,
    MargedTitle,
    StyledIcon,
    TitleContainer,
} from './Error.style';
import { ErrorEnum, ErrorProps } from './Error.type';

type CommonErrorConfiguration = { titleText: TranslationKey; text: TranslationKey };

const ErrorConfiguration: Record<
    ErrorEnum,
    | CommonErrorConfiguration
    | (CommonErrorConfiguration & {
          redirect: 'cart' | 'home' | 'reload' | 'back' | 'backToChoosePayment';
          buttonText: TranslationKey;
      })
    | CommonErrorConfiguration
> = {
    [ErrorEnum.CART_ERROR]: {
        titleText: 'cart_error_title',
        text: 'cart_error_content',
        buttonText: 'back_to_cart',
        redirect: 'cart',
    },
    [ErrorEnum.ORDER_ERROR]: {
        titleText: 'order_error_title',
        text: 'order_error_content',
        redirect: 'backToChoosePayment',
        buttonText: 'retry',
    },
    [ErrorEnum.TIME_ERROR]: {
        titleText: 'time_error_title',
        text: 'time_error_content',
        redirect: 'backToChoosePayment',
        buttonText: 'retry',
    },
    [ErrorEnum.NETWORK_ERROR]: {
        titleText: 'network_error_title',
        text: 'network_error_content',
        redirect: 'back',
        buttonText: 'retry',
    },
    [ErrorEnum.NOT_FOUND]: {
        titleText: 'error_title',
        text: 'not_found_message',
        buttonText: 'not_found_back_to_home',
        redirect: 'home',
    },
    [ErrorEnum.NO_RESTAURANT]: {
        titleText: 'error_title',
        text: 'restaurants.no_restaurant_error',
        redirect: 'home',
        buttonText: 'not_found_back_to_home',
    },
    [ErrorEnum.UNKNOWN_ERROR]: {
        titleText: 'error_title',
        text: 'unknown_error',
        buttonText: 'reload_page',
        redirect: 'reload',
    },
    [ErrorEnum.NO_CONSUMPTION_MODE]: {
        titleText: 'error_title',
        text: 'no_consumption_mode',
    },
    [ErrorEnum.QR_CODE_ERROR]: {
        titleText: 'error_title',
        text: 'qrcode_error',
    },
    [ErrorEnum.ORDER_RECEIPT_ERROR]: {
        titleText: 'error_title',
        text: 'order_receipt.error',
    },
    [ErrorEnum.MENU_NOT_ACTIVATED]: {
        titleText: 'error_title',
        text: 'menu_not_activated',
        buttonText: 'reload_page',
        redirect: 'reload',
    },
};

const Error: React.FunctionComponent<ErrorProps> = ({ error, errorMessage }) => {
    const stateErrorMessage = useLocation().state?.message;
    const { t } = useTranslation();
    const { redirectToHome } = useRedirectToHome();
    const { navigateBackToChoosePayment, handleGoBack } = useCustomNavigate();
    const { goCart } = useWorkflowStateMachine();
    const locationReload = () => window.location.reload();
    const errorConfig = ErrorConfiguration[error];

    let onClick: () => void = goCart;

    if ('redirect' in errorConfig) {
        if (errorConfig.redirect === 'home') {
            onClick = redirectToHome;
        } else if (errorConfig.redirect === 'reload') {
            onClick = locationReload;
        } else if (errorConfig.redirect === 'back') {
            onClick = handleGoBack;
        } else if (errorConfig.redirect === 'backToChoosePayment') {
            onClick = navigateBackToChoosePayment;
        }
    }

    return (
        <>
            <TitleContainer>
                <StyledIcon icon="close-circle-line" />
                <MargedTitle text={errorConfig.titleText} type={5} />
            </TitleContainer>
            <MargedCaption text={errorConfig.text} />
            {errorMessage && <MargedCaption text={errorMessage} noTranslation weight="Bold" />}
            {stateErrorMessage && (
                <MargedCaption text={stateErrorMessage} noTranslation weight="Bold" />
            )}
            {'buttonText' in errorConfig && (
                <MargedButton
                    fullWidth
                    onClick={onClick}
                    color="destructive"
                    elementLeft={<SvyIcon icon="arrow-left-s-line" />}
                >
                    {t(errorConfig.buttonText)}
                </MargedButton>
            )}
        </>
    );
};

export default Error;
